/** @jsxImportSource @emotion/react */
import { withMyTheme } from "../../../theme/theme"
import { css } from "@mui/material"
import { Typography, Box, RadioGroup, FormControlLabel, Radio } from "@mui/material"
import { useTranslation } from "react-i18next"
import { mobileCss } from "../../../util/isMobile"
import { useQuestionnaireForm } from "../context/QuestionnaireFormContext"
import { LocationAutocomplete } from "./components/LocationAutocomplete"
import { LocationDetailsField } from "./components/LocationDetailsField"

const StepTitleStyle = withMyTheme((theme) => css`
    font-size: 2rem;
    font-weight: 600;
    color: ${theme.palette.primary.main};
    text-align: center;
    margin-bottom: 1rem;
    
    ${mobileCss(`
        font-size: 1.5rem;
    `)}
`)

const StepContentStyle = withMyTheme((theme) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1.5vh;
    text-align: center;
    max-width: 600px;
    margin: 0 auto;
    padding: 0 1rem;
    
    ${mobileCss(`
        max-width: 90vw;
        gap: 1vh;
    `)}
`)

const FormContainerStyle = withMyTheme((theme) => css`
    display: flex;
    flex-direction: column;
    gap: 2rem;
    width: 100%;
    
    ${mobileCss(`
        gap: 1.5rem;
    `)}
`)

const RadioGroupContainerStyle = withMyTheme((theme) => css`
    text-align: left;
    width: 100%;
`)

const RadioGroupStyle = withMyTheme((theme) => css`
    flex-direction: row !important;
    gap: 1rem;
    
    ${mobileCss(`
        flex-direction: column !important;
    `)}
`)

const RadioLabelStyle = withMyTheme((theme) => css`
    font-size: 1rem;
    font-weight: 500;
    color: ${theme.palette.text.primary};
    margin-bottom: 0.5rem;
    text-align: left;
`)

const StyledRadioStyle = withMyTheme((theme) => css`
    &.MuiRadio-root {
        color: ${theme.palette.primary.main};
        
        &.Mui-checked {
            color: ${theme.palette.primary.main};
        }
    }
`)

interface WeddingLocationStepProps {
    onStart?: () => void
}

export const WeddingLocationStep = ({}: WeddingLocationStepProps) => {
    const { t } = useTranslation()
    const { formData, updateWeddingLocation } = useQuestionnaireForm()

    const handleSameLocationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const isSame = event.target.value === 'true'
        updateWeddingLocation({ 
            isSameLocation: isSame,
            location: isSame ? '' : formData.weddingLocation.location 
        })
    }

    const handleLocationChange = (location: string) => {
        updateWeddingLocation({ location })
    }

    const handleDetailsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        updateWeddingLocation({ details: event.target.value })
    }

    return (
        <div css={StepContentStyle}>
            <Typography css={StepTitleStyle}>
                {t('questionnaire.weddingLocation.title')}
            </Typography>
            
            <Box css={FormContainerStyle}>
                {/* Same Location Radio */}
                <Box css={RadioGroupContainerStyle}>
                    <Typography css={RadioLabelStyle}>
                        {t('questionnaire.weddingLocation.isSameLocation')}
                    </Typography>
                    <RadioGroup
                        value={formData.weddingLocation.isSameLocation.toString()}
                        onChange={handleSameLocationChange}
                        css={RadioGroupStyle}
                    >
                        <FormControlLabel
                            value="true"
                            control={<Radio css={StyledRadioStyle} />}
                            label={t('questionnaire.weddingLocation.isSameLocationYes')}
                        />
                        <FormControlLabel
                            value="false"
                            control={<Radio css={StyledRadioStyle} />}
                            label={t('questionnaire.weddingLocation.isSameLocationNo')}
                        />
                    </RadioGroup>
                </Box>

                {/* Wedding Location (conditional) */}
                {!formData.weddingLocation.isSameLocation && (
                    <LocationAutocomplete
                        label={t('questionnaire.weddingLocation.location')}
                        placeholder={t('questionnaire.weddingLocation.locationPlaceholder')}
                        value={formData.weddingLocation.location}
                        onChange={handleLocationChange}
                    />
                )}

                {/* Wedding Details */}
                <LocationDetailsField
                    label={t('questionnaire.weddingLocation.details')}
                    placeholder={t('questionnaire.weddingLocation.detailsPlaceholder')}
                    value={formData.weddingLocation.details}
                    onChange={handleDetailsChange}
                />
            </Box>
        </div>
    )
}
